import { tr, getLanguage, spanishShortDate } from "./i18n.js";
/**
 * PublishOS billing report for a newsletter's ads — display rules.
 *
 * The report (`newsletter.publishos`, built by backend/publishos_link.py)
 * holds one row per ad with an `outcome`. This turns rows into the text
 * the editor reads and decides which buttons to offer.
 *
 * Pure and framework-free — node --test frontend/scripts/publishosView.test.mjs
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-12" -> "Oct 12, 2026" (parsed by hand: no timezone shift). */
export function formatDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return "";
  const month = MONTHS[Number(m[2]) - 1];
  if (month && getLanguage() === "es") {
    return spanishShortDate(m[1], Number(m[2]) - 1, Number(m[3]));
  }
  return month ? `${month} ${Number(m[3])}, ${m[1]}` : "";
}

export const SETTLED = [
  "marked", "moved", "scheduled", "created", "already", "exists", "created_unscheduled",
];

/** What happened to one ad, in a sentence. */
export function outcomeText(row) {
  const r = row || {};
  switch (r.outcome) {
    case "marked":
      return tr("Existing booking marked as sent");
    case "moved": {
      const from = formatDay(r.previous_date);
      return from
        ? tr("Existing booking moved from {from} and marked as sent", { from })
        : tr("Existing booking moved to the send date and marked as sent");
    }
    case "scheduled":
      return tr("Unscheduled booking dated and marked as sent");
    case "created":
      return tr("New booking created and marked as sent");
    case "already":
      return tr("Already recorded in PublishOS");
    case "exists":
      return tr("Client already has a booking — left as is");
    case "created_unscheduled":
      return tr("New unscheduled booking created (not marked as sent)");
    case "needs_decision": {
      const day = formatDay(r.booking_date);
      return day
        ? tr("This client's only booking is scheduled for {day}", { day })
        : tr("This client's only booking is scheduled for a later date");
    }
    case "not_linked":
      return tr("Not linked to a PublishOS client — nothing done in PublishOS");
    case "no_product":
      return tr("No PublishOS product chosen for ad position {position} — ask an admin", { position: r.position || "?" });
    case "not_reported":
      return tr("Marked \"Do not report to PublishOS\" — nothing done in PublishOS");
    case "unreachable":
      return r.detail || tr("Couldn't reach PublishOS");
    case "error":
      return r.detail || tr("PublishOS could not record this ad");
    default:
      return tr("Waiting to be reported");
  }
}

/** "ok" | "warn" | "bad" | "muted" — picks the row colour. */
export function outcomeTone(row) {
  const o = (row || {}).outcome;
  if (SETTLED.includes(o)) return "ok";
  if (o === "needs_decision" || o === "not_linked" || o === "no_product") return "warn";
  if (o === "error" || o === "unreachable") return "bad";
  return "muted";
}

/** Heading line for the whole report. */
export function stateText(report) {
  const r = report || {};
  switch (r.state) {
    case "done":
      return r.mode === "draft" ? tr("Bookings checked in PublishOS") : tr("Reported to PublishOS");
    case "needs_decision":
      return tr("PublishOS needs your decision");
    case "failed":
      return r.error || tr("Some ads were not recorded in PublishOS");
    case "pending":
      return tr("Will be reported to PublishOS once it has gone out");
    case "cancelled":
      return tr("Not reported — this newsletter was never sent");
    default:
      return "";
  }
}

export const rowsOf = (report) =>
  Array.isArray((report || {}).items) ? report.items : [];

export const decisionRows = (report) =>
  rowsOf(report).filter((r) => r.outcome === "needs_decision");

/** Retry makes sense when something failed, or an ad could still be
 *  linked / given a product / unticked from "Do not report" and then
 *  picked up. Never while pending. */
export function canRetry(report) {
  const r = report || {};
  if (!r.state || r.state === "pending") return false;
  return rowsOf(r).some((row) =>
    ["error", "unreachable", "not_linked", "no_product", "not_reported", null, undefined]
      .includes(row.outcome),
  ) || (r.state === "failed" && rowsOf(r).length === 0);
}

/** Does at least one of these newsletter ads go to PublishOS? `library`
 *  is the stored advertisers; an ad missing from it counts as reportable
 *  (PublishOS then lists it as not linked). */
export function anyReportable(ads, library) {
  const skipped = new Set(
    (library || []).filter((a) => a.publishos_skip).map((a) => a.id),
  );
  return (ads || []).some((a) => !skipped.has(a.ad_id));
}

/** Should History flag this row? "decision" | "failed" | "". */
export function attention(state) {
  if (state === "needs_decision") return "decision";
  if (state === "failed") return "failed";
  return "";
}

/** Sent newsletters with ads and no report yet can be reported by hand
 *  (e.g. ones that went out before the link was set up). */
export function canStart(detail, linkEnabled) {
  const d = detail || {};
  return !!linkEnabled && !d.publishos && d.status === "sent" &&
    Array.isArray(d.ads) && d.ads.length > 0;
}
